import {
  httpBatchLink,
  httpLink,
  httpSubscriptionLink,
  isNonJsonSerializable,
  splitLink,
} from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import superjson from 'superjson';
import type { AppRouter } from '@sharpe/core/trpc/index';
import type { inferRouterOutputs } from '@trpc/server';

// Type inference helper for router outputs
export type RouterOutputs = inferRouterOutputs<AppRouter>;

// Create typed tRPC hooks
export const trpc = createTRPCReact<AppRouter>();

// Get the API base URL - in dev, Vite proxies /api to the backend
function getBaseUrl() {
  // In production, use the configured API URL
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // In dev, use relative URL (Vite proxy handles it)
  return '';
}

// Billing queries that hit the Autumn API -- isolate from the batch so a slow
// or unavailable Autumn service doesn't block fast DB queries (sidebar, chats).
const BILLING_ROUTES = new Set([
  'usage.checkMyQuota',
  'usage.autumnBalance',
  'usage.getBillingInfo',
  'usage.getAutoTopUpSettings',
  'auth.planInfo',
]);

function isBillingOp(op: { path: string }) {
  return BILLING_ROUTES.has(op.path);
}

/**
 * Create the tRPC client with configured links.
 * Used by the TRPCProvider in providers/index.tsx.
 */
export function createTRPCClient() {
  const url = `${getBaseUrl()}/api/v1`;

  const fetchWithCredentials = (input: RequestInfo | URL, init?: RequestInit) =>
    fetch(input, { ...init, credentials: 'include' });

  return trpc.createClient({
    links: [
      splitLink({
        condition: (op) => op.type === 'subscription',
        true: httpSubscriptionLink({
          url,
          transformer: superjson,
        }),
        false: splitLink({
          condition: (op) => isNonJsonSerializable(op.input),
          true: httpLink({
            url,
            transformer: {
              serialize: (data) => data,
              deserialize: superjson.deserialize,
            },
            fetch: fetchWithCredentials,
          }),
          false: splitLink({
            // Billing queries go through their own unbatched link so they
            // can't stall the main batch when Autumn is slow/down
            condition: (op) => isBillingOp(op),
            true: httpLink({
              url,
              transformer: superjson,
              fetch: fetchWithCredentials,
            }),
            false: httpBatchLink({
              url,
              transformer: superjson,
              fetch: fetchWithCredentials,
            }),
          }),
        }),
      }),
    ],
  });
}
